/**
 * Auth context for the payment-tracker admin frontend (M25).
 *
 * Wraps the centralised ``api`` client with React state for the
 * current user, the bearer token, and a ``loading`` flag the routes
 * use to gate their first render.
 *
 * Public surface:
 *
 *     const { user, token, loading, login, register, logout } = useAuth();
 *
 * The ``bc:auth:logout`` window event lets ``api.js`` force a logout
 * from a 401 response without importing this module.
 */

import { createContext, useCallback, useContext, useEffect, useState } from "react";

import api from "@/services/api";

const TOKEN_KEY = "pmt_token";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  const handleLogout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  // Hydrate the user object on mount / token change.
  useEffect(() => {
    let cancelled = false;
    const fetchUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await api.get("/auth/me");
        if (!cancelled) setUser(res.data);
      } catch {
        if (!cancelled) handleLogout();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchUser();
    return () => {
      cancelled = true;
    };
  }, [token, handleLogout]);

  // Listen for forced logouts dispatched by api.js on a 401 response.
  useEffect(() => {
    const onForcedLogout = () => handleLogout();
    window.addEventListener("bc:auth:logout", onForcedLogout);
    return () => window.removeEventListener("bc:auth:logout", onForcedLogout);
  }, [handleLogout]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem(TOKEN_KEY, access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = handleLogout;

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
